import { ApiError } from "./api";

export type DownloadedFile = { blob: Blob; filename: string };

/** The file name from a Content-Disposition header; RFC 5987 `filename*` (non-ASCII) wins. */
export function filenameFrom(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const encoded = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // malformed encoding: try the plain name below
    }
  }
  return /filename="((?:[^"\\]|\\.)*)"/.exec(disposition)?.[1]?.replace(/\\(.)/g, "$1") ?? fallback;
}

/** GET a file from the API with the session cookie; errors come back as ApiError. */
export async function fetchFile(url: string, fallback: string): Promise<DownloadedFile> {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, body);
  }
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("content-disposition"), fallback),
  };
}

/** Hand a fetched file to the browser as a download. */
export function saveFile({ blob, filename }: DownloadedFile): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
